
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Settings2, Trash2 } from 'lucide-react';
import { useCommunityFeatures } from '@/hooks/useFeatures';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface FeaturesManagerProps {
  communityId: string;
}

const FeaturesManager = ({ communityId }: FeaturesManagerProps) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    feature_key: '',
    description: ''
  });

  const { data: features, isLoading } = useCommunityFeatures(communityId);
  const queryClient = useQueryClient();

  const handleCreateFeature = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const { error } = await supabase
        .from('community_subscription_features')
        .insert({
          name: formData.name,
          feature_key: formData.feature_key,
          description: formData.description,
          community_id: communityId
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Feature created successfully',
      });

      // Reset form and close dialog
      setFormData({ name: '', feature_key: '', description: '' });
      setIsCreateDialogOpen(false);

      // Refresh the features list
      queryClient.invalidateQueries({ queryKey: ['community-features', communityId] });

    } catch (error) {
      console.error('Error creating feature:', error);
      toast({
        title: 'Error',
        description: 'Failed to create feature. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteFeature = async (featureId: string, featureName: string) => {
    if (!confirm(`Are you sure you want to delete the feature "${featureName}"? This will remove it from all plans.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('community_subscription_features')
        .delete()
        .eq('id', featureId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Feature deleted successfully',
      });

      // Refresh the features list
      queryClient.invalidateQueries({ queryKey: ['community-features', communityId] });

    } catch (error) {
      console.error('Error deleting feature:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete feature. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Subscription Features
            </CardTitle>
            <CardDescription>
              Manage the features available in subscription plans
            </CardDescription>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Add Feature
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Feature</DialogTitle>
                <DialogDescription>
                  Add a new feature that can be assigned to subscription plans.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateFeature} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Feature Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Advanced Analytics"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feature_key">Feature Key</Label>
                  <Input
                    id="feature_key"
                    value={formData.feature_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, feature_key: e.target.value }))}
                    placeholder="e.g., advanced_analytics"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Unique identifier used in code (lowercase, underscores only)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this feature provides..."
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isCreating}>
                    {isCreating ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Create Feature
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading features...</span>
          </div>
        ) : (
          <>
            {features && features.length > 0 ? (
              <div className="space-y-3">
                {features.map((feature) => (
                  <Card key={feature.id} className="border-l-4 border-l-primary/20">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-medium">{feature.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {feature.description}
                          </p>
                          <div className="mt-2">
                            <Badge variant="outline" className="text-xs">
                              {feature.feature_key}
                            </Badge>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteFeature(feature.id, feature.name)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Settings2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No features created yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create features to define what capabilities each subscription plan includes.
                </p>
                <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create First Feature
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FeaturesManager;
